import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { getAuthToken } from '../services/api';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ageFromDateOfBirth } from '../utils/ageFromDateOfBirth';

interface EditLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: any;
  onLeadUpdated: () => void;
}

const EditLeadModal: React.FC<EditLeadModalProps> = ({
  isOpen,
  onClose,
  lead,
  onLeadUpdated
}) => {
  const [formData, setFormData] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    studentName: '',
    studentAge: '',
    studentGender: '',
    dateOfBirth: '',
    religion: '',
    fatherName: '',
    motherName: '',
    alternatePhone: '',
    address: '',
    locality: '',
    pinCode: '',
    notes: '',
    currentSchool: '',
    preferredGrade: '',
    inquirySource: '',
    priority: '',
    team_id: '',
    referral_type: '',
    assignedTo: ''
  });
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (lead) {
      setFormData({
        parentName: lead.parentName || '',
        parentEmail: lead.parentEmail || '',
        parentPhone: lead.parentPhone || '',
        studentName: lead.studentName || '',
        studentAge: lead.studentAge?.toString() || '',
        studentGender: lead.studentGender || '',
        dateOfBirth: lead.dateOfBirth || '',
        religion: lead.religion || '',
        fatherName: lead.fatherName || '',
        motherName: lead.motherName || '',
        alternatePhone: lead.alternatePhone || '',
        address: lead.address || '',
        locality: lead.locality || '',
        pinCode: lead.pinCode || '',
        notes: lead.notes || '',
        currentSchool: lead.currentSchool || '',
        preferredGrade: lead.preferredGrade || '',
        inquirySource: lead.inquirySource || '',
        priority: lead.priority || '',
        team_id: lead.team_id || '',
        referral_type: lead.referral_type || '',
        assignedTo: lead.assignedTo || ''
      });
    }
  }, [lead]);

  useEffect(() => {
    if (!formData.dateOfBirth) return;
    const age = ageFromDateOfBirth(formData.dateOfBirth);
    if (!age || age === formData.studentAge) return;
    setFormData(prev => ({ ...prev, studentAge: age }));
  }, [formData.dateOfBirth, formData.studentAge]);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [isOpen]);

  const fetchUsers = async () => {
    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        const admissionsStaff = data.data.filter((u: any) => 
          u.isActive && ['admissions_counselor', 'admissions_manager', 'admin', 'superadmin'].includes(u.role)
        );
        setUsers(admissionsStaff);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const inquirySources = [
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'walk-in', label: 'Walk-in' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'advertisement', label: 'Advertisement' },
    { value: 'other', label: 'Other' }
  ];

  const priorities = [
    { value: 'hot', label: 'Hot' },
    { value: 'warm', label: 'Warm' },
    { value: 'cold', label: 'Cold' }
  ];

  const grades = [
    'Nursery', 'Jr.KG', 'Sr.KG', 'Class I', 'Class II', 'Class III',
    'Class IV', 'Class V', 'Class VI', 'Class VII'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/leads/${lead.leadId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          studentAge: parseInt(formData.studentAge) || 0
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update lead');
      }

      onLeadUpdated();
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-6 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-900">Edit Lead</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Student Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Student Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Student Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.studentName}
                  onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender
                </label>
                <select
                  value={formData.studentGender}
                  onChange={(e) => setFormData({ ...formData, studentGender: e.target.value })}
                  className="input-field"
                >
                  <option value="">Select Gender</option>
                  <option value="male">Boy</option>
                  <option value="female">Girl</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Religion
                </label>
                <input
                  type="text"
                  value={formData.religion}
                  onChange={(e) => setFormData({ ...formData, religion: e.target.value })}
                  className="input-field"
                  placeholder="e.g. Islam"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Age
                </label>
                <input
                  type="number"
                  min="0"
                  max="25"
                  value={formData.studentAge}
                  readOnly={Boolean(formData.dateOfBirth)}
                  onChange={(e) => setFormData({ ...formData, studentAge: e.target.value })}
                  className={`input-field ${formData.dateOfBirth ? 'bg-gray-50' : ''}`}
                />
                {formData.dateOfBirth && (
                  <p className="text-xs text-gray-500 mt-1">Calculated from date of birth</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preferred Grade *
                </label>
                <select
                  required
                  value={formData.preferredGrade}
                  onChange={(e) => setFormData({ ...formData, preferredGrade: e.target.value })}
                  className="input-field"
                >
                  <option value="">Select Grade</option>
                  {grades.map((grade) => (
                    <option key={grade} value={grade}>
                      {grade}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current School
                </label>
                <input
                  type="text"
                  value={formData.currentSchool}
                  onChange={(e) => setFormData({ ...formData, currentSchool: e.target.value })}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          {/* Parent / Guardian Information */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Parent / Guardian Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Father's Name
                </label>
                <input
                  type="text"
                  value={formData.fatherName}
                  onChange={(e) => setFormData({ ...formData, fatherName: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mother's Name
                </label>
                <input
                  type="text"
                  value={formData.motherName}
                  onChange={(e) => setFormData({ ...formData, motherName: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Contact Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.parentName}
                  onChange={(e) => setFormData({ ...formData, parentName: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.parentEmail}
                  onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Mobile *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.parentPhone}
                  onChange={(e) => setFormData({ ...formData, parentPhone: e.target.value })}
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Alternate Mobile
                </label>
                <input
                  type="tel"
                  value={formData.alternatePhone}
                  onChange={(e) => setFormData({ ...formData, alternatePhone: e.target.value })}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          {/* Address Details */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Address Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Residential Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Area / Locality</label>
                <input
                  type="text"
                  value={formData.locality}
                  onChange={(e) => setFormData({ ...formData, locality: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PIN Code</label>
                <input
                  type="text"
                  maxLength={6}
                  value={formData.pinCode}
                  onChange={(e) => setFormData({ ...formData, pinCode: e.target.value.replace(/\D/g, '') })}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          {/* Lead Details */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Lead Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Inquiry Source *
                </label>
                <select
                  required
                  value={formData.inquirySource}
                  onChange={(e) => setFormData({ ...formData, inquirySource: e.target.value })}
                  className="input-field"
                >
                  {inquirySources.map((source) => (
                    <option key={source.value} value={source.value}>
                      {source.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority *
                </label>
                <select
                  required
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="input-field"
                >
                  {priorities.map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team
                </label>
                <select
                  value={formData.team_id}
                  onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
                  className="input-field"
                >
                  <option value="">Select Team</option>
                  <option value="blue">Blue Team</option>
                  <option value="green">Green Team</option>
                  <option value="red">Red Team</option>
                  <option value="yellow">Yellow Team</option>
                  <option value="orange">Orange Team</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Referral Type
                </label>
                <select
                  value={formData.referral_type}
                  onChange={(e) => setFormData({ ...formData, referral_type: e.target.value })}
                  className="input-field"
                >
                  <option value="">No Referral</option>
                  <option value="parent">Parent Referral</option>
                  <option value="student">Student Referral</option>
                  <option value="staff">Staff Referral</option>
                  <option value="community">Community Referral</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign To
                </label>
                <select
                  value={formData.assignedTo}
                  onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                  className="input-field"
                >
                  <option value="">Select User</option>
                  {users.map((user) => (
                    <option key={user.userId} value={user.userId}>
                      {user.name} ({user.role})
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field"
                  rows={2}
                  placeholder="Any additional information..."
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLeadModal;
